<script lang="ts">
	/**
	 * Superfície pública (§4). Operada por alguém externo, não autenticado, na
	 * internet aberta. Faz o mínimo: mostra o que está para assinar, captura a
	 * assinatura, submete.
	 *
	 * Não escolhe nada. Não conhece ids. Não tem lógica de negócio.
	 */

	import type { SessaoPublicaResposta } from '@ac/shared';
	import PadAssinatura from '@ac/ui/PadAssinatura.svelte';
	import { api, ErroDoServidor, temToken } from './api';
	import { idiomaDoUrl, textos } from './textos';

	// Até a sessão chegar, o idioma vem da pista no URL; depois manda o servidor.
	let idioma = $state(idiomaDoUrl());
	const t = $derived(textos(idioma));

	type Fase = 'a-carregar' | 'pronto' | 'concluido' | 'invalido';

	let fase = $state<Fase>('a-carregar');
	let sessao = $state<SessaoPublicaResposta | null>(null);
	let assinaturaPng = $state<string | null>(null);
	let confirmaLeitura = $state(false);
	let ocupado = $state(false);
	let erro = $state<string | null>(null);
	let motivoInvalido = $state<string | null>(null);
	let totalAssinado = $state(0);

	const podeAssinar = $derived(assinaturaPng !== null && confirmaLeitura && !ocupado);

	$effect(() => {
		carregar();
	});

	async function carregar() {
		if (!temToken) {
			motivoInvalido = t.semToken;
			fase = 'invalido';
			return;
		}

		try {
			sessao = await api.sessao();
			idioma = sessao.idioma;
			fase = 'pronto';
		} catch (e) {
			if (e instanceof ErroDoServidor) {
				// Mensagens distintas por código: quem já assinou merece saber isso,
				// e não um genérico "link inválido".
				motivoInvalido =
					e.codigo === 'token_usado'
						? t.linkUsado
						: e.codigo === 'token_expirado'
							? t.linkExpirado
							: e.message;
			}
			fase = 'invalido';
		}
	}

	async function assinar() {
		if (!podeAssinar || !assinaturaPng) return;
		ocupado = true;
		erro = null;

		try {
			const resposta = await api.assinar(assinaturaPng);
			totalAssinado = resposta.documentos.length;
			fase = 'concluido';
		} catch (e) {
			erro = e instanceof Error ? e.message : t.falhaAssinatura;
		} finally {
			ocupado = false;
		}
	}

</script>

<main class="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
	{#if fase === 'a-carregar'}
		<p class="py-20 text-center text-slate-400">{t.aCarregar}</p>
	{:else if fase === 'invalido'}
		<div class="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
			<h1 class="mb-2 text-lg font-semibold text-slate-900">{t.linkIndisponivel}</h1>
			<p class="text-sm text-slate-600">{motivoInvalido ?? t.linkInvalido}</p>
		</div>
	{:else if fase === 'concluido'}
		<div class="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
			<h1 class="mb-2 text-lg font-semibold text-emerald-900">{t.assinada}</h1>
			<p class="text-sm text-emerald-800">{t.registadas(totalAssinado)}</p>
		</div>
	{:else if sessao}
		<header class="mb-6">
			<h1 class="text-xl font-semibold text-slate-900">{t.titulo}</h1>
			<p class="mt-1 text-sm text-slate-500">
				{sessao.clinica.nome}{#if sessao.clinica.nif}&nbsp;· {t.nif} {sessao.clinica.nif}{/if}
			</p>
		</header>

		{#if erro}
			<div
				role="alert"
				class="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
			>
				{erro}
			</div>
		{/if}

		<section class="mb-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
			<h2 class="mb-3 font-medium text-slate-900">{t.distribuidores}</h2>
			<ul class="mb-4 space-y-1.5">
				{#each sessao.distribuidores as distribuidor (distribuidor.nome)}
					<li class="flex items-start gap-2 text-sm text-slate-800">
						<span aria-hidden="true" class="mt-2 size-1.5 shrink-0 rounded-full bg-slate-400"></span>
						{distribuidor.nome}
					</li>
				{/each}
			</ul>
			<p class="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
				{t.notaDistribuidores}
			</p>
		</section>

		<section class="mb-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
			<h2 class="mb-3 font-medium text-slate-900">{t.declaracao}</h2>
			<div
				class="max-h-80 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-4
				       text-sm leading-relaxed whitespace-pre-line text-slate-700"
			>
				{sessao.textoConsentimento}
			</div>
			<p class="mt-2 text-xs text-slate-400">{t.versao(sessao.versaoConsentimento)}</p>
		</section>

		<section class="mb-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
			<h2 class="mb-1 font-medium text-slate-900">{t.assinatura}</h2>
			<p class="mb-3 text-sm text-slate-500">{sessao.nomeSignatario}</p>
			<PadAssinatura onchange={(png) => (assinaturaPng = png)} desativado={ocupado} {idioma} />
		</section>

		<label class="mb-5 flex cursor-pointer items-start gap-3 px-1">
			<input
				type="checkbox"
				bind:checked={confirmaLeitura}
				disabled={ocupado}
				class="mt-0.5 size-5 shrink-0 rounded border-slate-300 text-hfx-500 focus:ring-hfx-400"
			/>
			<span class="text-sm text-slate-700">{t.confirmacao}</span>
		</label>

		<button
			type="button"
			onclick={assinar}
			disabled={!podeAssinar}
			class="w-full rounded-lg bg-hfx-500 px-4 py-4 font-medium text-white
			       disabled:cursor-not-allowed disabled:opacity-40 hover:bg-hfx-600"
		>
			{ocupado ? t.aRegistar : t.assinar}
		</button>
	{/if}
</main>
